import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Router from 'next/router';
import i18n from '../../utils/i18n';

import { requestInteraction, closeInteractionDialog } from '../../actions/orderActions';

import ZendeskChat from '../ZendeskChat';
import ModalTitle from '../../components/ModalTitle';
import OrderHelpTemplateLoading from '../../components/OrderHelpTemplateLoading';
import OrderHelpTemplateActionToAction from '../../components/OrderHelpTemplateActionToAction';
import OrderHelpTemplateActionsBottomSheet from '../../components/OrderHelpTemplateActionsBottomSheet';
import OrderHelpTemplateItemsSelectionWithModifiers from '../../components/OrderHelpTemplateItemsSelectionWithModifiers';
import OrderHelpTemplateResolutionSelection from '../../components/OrderHelpTemplateResolutionSelection';
import OrderHelpTemplateClosableAction from '../../components/OrderHelpTemplateClosableAction';
import OrderHelpTemplateFeedbackScore from '../../components/OrderHelpTemplateFeedbackScore';
import OrderHelpTemplateFeedbackText from '../../components/OrderHelpTemplateFeedbackText';
import OrderHelpTemplateEmail from '../../components/OrderHelpTemplateEmail';
import OrderHelpTemplateCallNumber from '../../components/OrderHelpTemplateCallNumber';
import OrderHelpTemplateDialog from '../../components/OrderHelpTemplateDialog';
import OrderHelpTemplateCancelOrder from '../../components/OrderHelpTemplateCancelOrder';

import escapeExternalRedirectPath from '../../utils/escapeExternalRedirectPath';
import OrderHelpTemplateEmptyState from '../../components/OrderHelpTemplateEmptyState';
import {
  ACTION_TO_ACTION,
  ACTION_BOTTOM_SHEET,
  ITEMS_SELECTION_WITH_MODIFIERS,
  RESOLUTION_SELECTION,
  CLOSABLE_ACTION,
  FEEDBACK_SCORE,
  FEEDBACK_TEXT,
  EMAIL,
  CALL_NUMBER,
  CANCEL_ORDER,
  LIVE_CHAT,
  DIALOG,
  EMPTY_STATE,
  ACTION_INVOKING_TEMPLATES,
} from './templateTypes';

function getTemplateComponent(type, props) {
  switch (type) {
    case ACTION_TO_ACTION:
      return <OrderHelpTemplateActionToAction {...props} />;
    case ACTION_BOTTOM_SHEET:
      return <OrderHelpTemplateActionsBottomSheet {...props} />;
    case ITEMS_SELECTION_WITH_MODIFIERS:
      return <OrderHelpTemplateItemsSelectionWithModifiers {...props} />;
    case RESOLUTION_SELECTION:
      return <OrderHelpTemplateResolutionSelection {...props} />;
    case CLOSABLE_ACTION:
      return <OrderHelpTemplateClosableAction {...props} />;
    case FEEDBACK_SCORE:
      return <OrderHelpTemplateFeedbackScore {...props} />;
    case FEEDBACK_TEXT:
      return <OrderHelpTemplateFeedbackText {...props} />;
    case EMAIL:
      return <OrderHelpTemplateEmail {...props} />;
    case CALL_NUMBER:
      return <OrderHelpTemplateCallNumber {...props} />;
    case CANCEL_ORDER:
      return <OrderHelpTemplateCancelOrder {...props} />;
    case EMPTY_STATE:
      return <OrderHelpTemplateEmptyState {...props} />;
    default:
      return null;
  }
}

function showLiveChatFromParent() {
  const $zopim = typeof window !== 'undefined' && window.top.$zopim;
  if ($zopim && $zopim.livechat && $zopim.livechat.window) {
    $zopim.livechat.window.show();
  }
}

function hideLiveChatFromParent() {
  const $zopim = typeof window !== 'undefined' && window.top.$zopim;
  if ($zopim && $zopim.livechat) {
    $zopim.livechat.hideAll();
  }
}

export function OrderHelpTemplateContainer({
  header,
  redirectTo,
  iframe,
  title,
  type,
  subType,
  data,
  orderId,
  actionId,
  interactionId,
  showDialog,
  isDialogOnlyHelpPage,
  showSelfHelpPreviousButton,
  requestInteraction: requestInteractionAction,
  closeInteractionDialog: closeInteractionDialogAction,
  loading,
  nonIframeSelfHelpFeatureOn,
}) {
  const finishInteraction = () => {
    if (iframe) {
      window.top.closeSelfHelp();
    } else {
      Router.push(escapeExternalRedirectPath(redirectTo));
    }
  };

  const templateComponent = getTemplateComponent(type, {
    iframe,
    data,
    orderId,
    subType,
    actionId,
    interactionId,
    hideFooterPrevious: !showSelfHelpPreviousButton,
    requestInteraction: requestInteractionAction,
    finishInteraction,
  });

  if (subType === LIVE_CHAT && !loading) {
    showLiveChatFromParent();
  } else {
    hideLiveChatFromParent();
  }

  let modalTitle = title || i18n.gettext('order_help.error_loading_shs.title');
  if (!iframe && isDialogOnlyHelpPage) {
    modalTitle = data.dialogTitle;
  }

  return (
    <Fragment>
      {!nonIframeSelfHelpFeatureOn &&
        !(iframe && isDialogOnlyHelpPage) && (
          <ModalTitle
            title={modalTitle}
            header={header}
            text={data.text}
            iframe={iframe}
            heart={type === 'resolution_selection'}
            hidePrevious={iframe || !showSelfHelpPreviousButton}
            onClose={finishInteraction}
          />
        )}

      {templateComponent}

      {(loading || ACTION_INVOKING_TEMPLATES.includes(type)) && <OrderHelpTemplateLoading />}

      {!iframe && <ZendeskChat />}

      {subType === DIALOG &&
        showDialog && (
          <OrderHelpTemplateDialog
            data={data}
            actionId={actionId}
            interactionId={interactionId}
            requestInteraction={requestInteractionAction}
            closeInteractionDialog={closeInteractionDialogAction}
            isDialogOnlyHelpPage={isDialogOnlyHelpPage}
            iframe={iframe}
            finishInteraction={finishInteraction}
          />
        )}
    </Fragment>
  );
}

OrderHelpTemplateContainer.propTypes = {
  title: PropTypes.string,
  type: PropTypes.string,
  subType: PropTypes.string,
  data: PropTypes.shape({
    text: PropTypes.string,
    contactReason: PropTypes.string,
    department: PropTypes.string,
  }),
  header: PropTypes.string,
  iframe: PropTypes.bool,
  redirectTo: PropTypes.string,
  orderId: PropTypes.string,
  interactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  actionId: PropTypes.string,
  showDialog: PropTypes.bool,
  requestInteraction: PropTypes.func.isRequired,
  closeInteractionDialog: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  /**
   * If the dialog is the only thing that's showing (and there's no self help under it)
   */
  isDialogOnlyHelpPage: PropTypes.bool,
  /**
   * Whether to show the previous button for the self help
   */
  showSelfHelpPreviousButton: PropTypes.bool,
  /**
   * Feature flag: whether to use non-iframe self help (needed for order web order history page)
   * or CCL Dialog component self help (when order history is moved to CWA)
   */
  nonIframeSelfHelpFeatureOn: PropTypes.bool,
};

OrderHelpTemplateContainer.defaultProps = {
  title: '',
  type: '',
  subType: '',
  data: {
    text: '',
    contactReason: null,
    department: null,
  },
  header: null,
  iframe: false,
  redirectTo: null,
  orderId: '',
  actionId: '',
  showDialog: false,
  interactionId: null,
  isDialogOnlyHelpPage: false,
  showSelfHelpPreviousButton: false,
  nonIframeSelfHelpFeatureOn: false,
};

const mapStateToProps = ({ order, config }) => {
  const help = order.help;

  return {
    title: help.title,
    data: help.data,
    type: help.type,
    subType: help.subType,
    header: help.header,
    iframe: help.iframe,
    redirectTo: help.redirectTo,
    orderId: help.id,
    actionId: help.actionId,
    interactionId: help.interactionId,
    showDialog: help.showDialog,
    loading: help.loading,
    isDialogOnlyHelpPage: help.isFirstHelpPage && help.subType === DIALOG,
    showSelfHelpPreviousButton: help.showSelfHelpPreviousButton,
    nonIframeSelfHelpFeatureOn: config.features.web_order_help_non_iframe_self_help || false,
  };
};

const mapDispatchToProps = (dispatch) => ({
  requestInteraction: (...args) => dispatch(requestInteraction(...args)),
  closeInteractionDialog: (...args) => dispatch(closeInteractionDialog(...args)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OrderHelpTemplateContainer);
